import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faSpinner } from "@fortawesome/free-solid-svg-icons";

function encodeWAV(audioBuffer) {
  const numChannels = audioBuffer.numberOfChannels;
  const sampleRate = audioBuffer.sampleRate;
  const numSamples = audioBuffer.length;
  const dataSize = numSamples * numChannels * 2;

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++)
      view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    for (let ch = 0; ch < numChannels; ch++) {
      const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(ch)[i]));
      view.setInt16(offset, sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
}

function encodeMp3WithWorker(audioBuffer) {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL("./mp3Worker.js", import.meta.url));

    const left = audioBuffer.getChannelData(0).slice();
    const right = audioBuffer.numberOfChannels > 1
      ? audioBuffer.getChannelData(1).slice()
      : left.slice();

    worker.onmessage = ({ data: { chunks } }) => {
      worker.terminate();
      resolve(new Blob(chunks, { type: "audio/mp3" }));
    };

    worker.onerror = (e) => {
      worker.terminate();
      reject(e);
    };

    worker.postMessage(
      { left, right, sampleRate: audioBuffer.sampleRate },
      [left.buffer, right.buffer]
    );
  });
}

async function renderOffline({ audioBuffer, playbackRate, reverbAmount, bassBoost, volume, impulseBuffer }) {
  const outputDuration = audioBuffer.duration / playbackRate;
  const offlineCtx = new OfflineAudioContext(
    2,
    Math.ceil(outputDuration * audioBuffer.sampleRate),
    audioBuffer.sampleRate
  );

  const source = offlineCtx.createBufferSource();
  source.buffer = audioBuffer;
  source.playbackRate.value = playbackRate;

  const gainNode = offlineCtx.createGain();
  gainNode.gain.value = volume;

  const bassBoostNode = offlineCtx.createBiquadFilter();
  bassBoostNode.type = "lowshelf";
  bassBoostNode.frequency.value = 200;
  bassBoostNode.gain.value = bassBoost;

  gainNode.connect(bassBoostNode);
  bassBoostNode.connect(offlineCtx.destination);

  const dryGain = offlineCtx.createGain();
  dryGain.gain.value = 1 - reverbAmount;
  source.connect(dryGain);
  dryGain.connect(gainNode);

  const wetGain = offlineCtx.createGain();
  wetGain.gain.value = reverbAmount;
  source.connect(wetGain);

  const numChorus = 3;
  const chorusNodes = [];
  const chorusGains = [];

  for (let i = 0; i < numChorus; i++) {
    const delay = offlineCtx.createDelay();
    delay.delayTime.value = 0.03 * (i + 1) + Math.random() * 0.005;
    chorusNodes.push(delay);

    const gain = offlineCtx.createGain();
    gain.gain.value = 0.6;
    chorusGains.push(gain);

    const modulator = offlineCtx.createOscillator();
    modulator.frequency.value = 0.25;
    modulator.type = "sine";
    modulator.start();

    const modGain = offlineCtx.createGain();
    modGain.gain.value = 0.002;
    modulator.connect(modGain);
    modGain.connect(delay.delayTime);

    delay.connect(gain);
  }

  const highPass = offlineCtx.createBiquadFilter();
  highPass.type = "highpass";
  highPass.frequency.value = 200;
  highPass.Q.value = 0.707;

  wetGain.connect(chorusNodes[0]);
  for (let i = 0; i < numChorus - 1; i++)
    chorusNodes[i].connect(chorusNodes[i + 1]);
  for (let i = 0; i < numChorus; i++)
    chorusGains[i].connect(highPass);

  const reverb = offlineCtx.createConvolver();
  reverb.buffer = impulseBuffer;

  const lowPass = offlineCtx.createBiquadFilter();
  lowPass.type = "lowpass";
  lowPass.frequency.value = 10000;
  lowPass.Q.value = 0.707;

  highPass.connect(reverb);
  reverb.connect(lowPass);
  lowPass.connect(gainNode);

  source.start(0);
  return offlineCtx.startRendering();
}

const DownloadButton = ({
  audioBuffer,
  playbackRate,
  reverbAmount,
  bassBoost,
  volume,
  impulseBuffer,
  fileName,
  styles,
}) => {
  const [rendering, setRendering] = useState(null); // null | "wav" | "mp3"

  const handleDownload = async (format) => {
    if (!audioBuffer || rendering) return;
    setRendering(format);

    try {
      const rendered = await renderOffline({
        audioBuffer,
        playbackRate,
        reverbAmount,
        bassBoost,
        volume,
        impulseBuffer,
      });

      const baseName = fileName
        ? fileName.replace(/\.[^/.]+$/, "") + "_nightcore"
        : "audio_nightcore";

      const blob =
        format === "mp3" ? await encodeMp3WithWorker(rendered) : encodeWAV(rendered);

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Render failed:", err);
      alert("Download failed: " + (err?.message || err));
    } finally {
      setRendering(null);
    }
  };

  const disabled = !audioBuffer || rendering !== null;

  return (
    <div className={`${styles.downloadGroup} ${disabled ? styles.downloadGroupDisabled : ""}`}>
      <button
        className={`${styles.downloadBtn} ${styles.downloadBtnPrimary}`}
        onClick={() => handleDownload("wav")}
        disabled={disabled}
      >
        <FontAwesomeIcon
          icon={rendering === "wav" ? faSpinner : faDownload}
          className={rendering === "wav" ? styles.downloadSpinner : styles.downloadIcon}
        />
        <span>{rendering === "wav" ? "Rendering…" : "Export WAV"}</span>
      </button>
      <button
        className={`${styles.downloadBtn} ${styles.downloadBtnSecondary}`}
        onClick={() => handleDownload("mp3")}
        disabled={disabled}
      >
        <span>{rendering === "mp3" ? "Rendering…" : "MP3"}</span>
      </button>
    </div>
  );
};

export default DownloadButton;
